import React, { useState } from "react";
import { Cloud, AlertTriangle, Loader2, CheckCircle } from "lucide-react";

export default function DataMigrationModal({
  isOpen,
  onMigrate,
  onSkip,
  localDataSummary,
  darkMode = false,
}) {
  const [isMigrating, setIsMigrating] = useState(false);
  const [migrationComplete, setMigrationComplete] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const handleMigrate = async () => {
    setIsMigrating(true);
    setError(null);
    try {
      await onMigrate();
      setMigrationComplete(true);
      setTimeout(() => {
        onSkip(); // Chiudi il modal dopo successo
      }, 1500);
    } catch (err) {
      console.error("Migration error:", err);
      setError("Errore durante la migrazione. Riprova.");
      setIsMigrating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 transition-opacity" />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className={`relative w-full max-w-md rounded-2xl shadow-xl p-6 ${
            darkMode ? "bg-neutral-900" : "bg-white"
          }`}
        >
          {migrationComplete ? (
            // Successo
            <div className="text-center py-4">
              <div
                className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                  darkMode ? "bg-emerald-900/30" : "bg-emerald-100"
                }`}
              >
                <CheckCircle
                  className={`w-8 h-8 ${
                    darkMode ? "text-emerald-400" : "text-emerald-600"
                  }`}
                />
              </div>
              <h3
                className={`text-lg font-semibold ${
                  darkMode ? "text-white" : "text-neutral-900"
                }`}
              >
                Migrazione completata!
              </h3>
              <p
                className={`mt-2 text-sm ${
                  darkMode ? "text-neutral-500" : "text-neutral-600"
                }`}
              >
                I tuoi dati sono ora sincronizzati nel cloud
              </p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="flex items-center gap-3 mb-4">
                <div
                  className={`p-2 rounded-lg ${
                    darkMode ? "bg-forfy-900/30" : "bg-forfy-50"
                  }`}
                >
                  <Cloud
                    className={`w-5 h-5 ${
                      darkMode ? "text-forfy-300" : "text-forfy-600"
                    }`}
                  />
                </div>
                <div>
                  <h3
                    className={`font-semibold ${
                      darkMode ? "text-white" : "text-neutral-900"
                    }`}
                  >
                    Dati locali trovati
                  </h3>
                  <p
                    className={`text-sm ${
                      darkMode ? "text-neutral-500" : "text-neutral-600"
                    }`}
                  >
                    Vuoi importarli nel cloud?
                  </p>
                </div>
              </div>

              {/* Riepilogo dati */}
              <div
                className={`p-4 rounded-lg mb-4 ${
                  darkMode ? "bg-neutral-800/50" : "bg-neutral-50"
                }`}
              >
                <h4
                  className={`text-xs font-semibold uppercase tracking-wide mb-2 ${
                    darkMode ? "text-neutral-500" : "text-neutral-500"
                  }`}
                >
                  Dati da importare
                </h4>
                <ul
                  className={`space-y-1 text-sm ${
                    darkMode ? "text-neutral-400" : "text-neutral-700"
                  }`}
                >
                  {localDataSummary.invoicesCount > 0 && (
                    <li>
                      {localDataSummary.invoicesCount} fattur
                      {localDataSummary.invoicesCount === 1 ? "a" : "e"}
                    </li>
                  )}
                  {localDataSummary.contributionsCount > 0 && (
                    <li>
                      {localDataSummary.contributionsCount} versament
                      {localDataSummary.contributionsCount === 1 ? "o" : "i"} pensionistici
                    </li>
                  )}
                  {localDataSummary.hasProfile && <li>Profilo utente</li>}
                  {localDataSummary.hasFiscalSettings && (
                    <li>Impostazioni fiscali</li>
                  )}
                </ul>
              </div>

              {/* Avviso */}
              <div
                className={`flex items-start gap-2 p-3 rounded-lg mb-4 ${
                  darkMode ? "bg-amber-900/30" : "bg-amber-50"
                }`}
              >
                <AlertTriangle
                  className={`w-4 h-4 mt-0.5 shrink-0 ${
                    darkMode ? "text-amber-400" : "text-amber-600"
                  }`}
                />
                <p
                  className={`text-xs ${
                    darkMode ? "text-amber-300" : "text-amber-700"
                  }`}
                >
                  Se salti questo passaggio, i dati locali rimarranno sul
                  dispositivo ma non saranno sincronizzati.
                </p>
              </div>

              {/* Errore */}
              {error && (
                <div
                  className={`mb-4 p-3 rounded-lg ${
                    darkMode ? "bg-red-900/30 text-red-300" : "bg-red-50 text-red-700"
                  }`}
                >
                  <p className="text-sm">{error}</p>
                </div>
              )}

              {/* Azioni */}
              <div className="flex gap-3">
                <button
                  onClick={onSkip}
                  disabled={isMigrating}
                  className={`flex-1 px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                    isMigrating
                      ? "opacity-50 cursor-not-allowed"
                      : darkMode
                      ? "bg-neutral-800 text-neutral-400 hover:bg-neutral-700"
                      : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
                  }`}
                >
                  Salta
                </button>
                <button
                  onClick={handleMigrate}
                  disabled={isMigrating}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                    isMigrating
                      ? "opacity-50 cursor-not-allowed bg-forfy-500 text-white"
                      : "bg-forfy-500 text-white hover:bg-forfy-600"
                  }`}
                >
                  {isMigrating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Importazione...
                    </>
                  ) : (
                    "Importa nel cloud"
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
